import React, { useState, useEffect, useCallback, useMemo } from "react";
import {
  Button,
  Grid,
  TextField,
  Paper,
  Chip,
  Box,
  IconButton,
  Tooltip,
  Typography,
  Collapse,
  Divider,
  FormControl,
  Select,
  MenuItem,
  Avatar,
  // Fade, // ✅ Removido para evitar warning do StrictMode com findDOMNode
} from "@material-ui/core";
import { makeStyles } from "@material-ui/core/styles";
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { DatePicker, LocalizationProvider } from '@mui/x-date-pickers';
import brLocale from 'date-fns/locale/pt-BR';
import { format, subDays, startOfMonth, endOfWeek, startOfWeek } from 'date-fns';
import { i18n } from "../../translate/i18n";
import FilterListIcon from "@material-ui/icons/FilterList";
import RestoreIcon from '@material-ui/icons/Restore';
import SearchIcon from '@material-ui/icons/Search';
import { useMountedState } from "../../utils/chartOptimizer";
import { useDashboardContext } from "../../context/Dashboard/DashboardContext";
import DashboardIcon from "@material-ui/icons/Dashboard";
import PieChartIcon from "@material-ui/icons/PieChart";
import TimelineIcon from "@material-ui/icons/Timeline";
import PeopleIcon from "@material-ui/icons/People";
import CategoryIcon from "@material-ui/icons/Category";
import ScheduleIcon from "@material-ui/icons/Schedule";
import UpdateIcon from '@material-ui/icons/Update';
import VisibilityIcon from '@material-ui/icons/Visibility';
import VisibilityOffIcon from '@material-ui/icons/VisibilityOff';
import ViewDayIcon from '@material-ui/icons/ViewDay';

const useStyles = makeStyles((theme) => ({
  filterContainer: {
    padding: theme.spacing(2),
    borderRadius: 12,
    marginBottom: theme.spacing(2),
    backgroundColor: theme.palette.background.paper,
    boxShadow: theme.palette.type === 'dark'
      ? '0 2px 10px rgba(0, 0, 0, 0.3)'
      : '0 2px 10px rgba(0, 0, 0, 0.05)',
    transition: 'all 0.3s ease',
    border: `1px solid ${theme.palette.divider}`,
  },
  filterHeader: {
    display: "flex",
    justifyContent: "space-between",
    alignItems: "center",
    marginBottom: theme.spacing(0.5),
  },
  quickFilterContainer: {
    display: "flex",
    gap: theme.spacing(0.3),
    flexWrap: "wrap",
    marginBottom: theme.spacing(0.5),
  },
  chip: {
    margin: theme.spacing(0.2),
    transition: "all 0.2s ease",
    height: 28,
    borderRadius: 14,
    fontWeight: 500,
    fontSize: '0.7rem',
  },
  activeChip: {
    backgroundColor: theme.palette.primary.main,
    color: theme.palette.primary.contrastText,
    fontWeight: 600,
    "&:hover": {
      backgroundColor: theme.palette.primary.dark,
    },
  },
  dateRow: {
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
  },
  datePickerField: {
    "& .MuiInputBase-root": {
      height: 34,
      borderRadius: 8,
      fontSize: '0.8rem',
    },
    "& .MuiInputBase-input": {
      padding: theme.spacing(0.5, 1),
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: 8,
    },
  },
  applyButton: {
    height: 34,
    minWidth: 'auto',
    borderRadius: 8,
    padding: theme.spacing(0, 1.5),
    backgroundImage: `linear-gradient(135deg, ${theme.palette.primary.main} 0%, ${theme.palette.primary.dark} 100%)`,
    color: theme.palette.primary.contrastText,
    fontWeight: 600,
    fontSize: '0.8rem',
    whiteSpace: 'nowrap',
  },
  resetButton: {
    padding: 6,
    borderRadius: '50%',
  },
  filterTitle: {
    fontSize: '0.85rem',
    fontWeight: 600,
    display: 'flex',
    alignItems: 'center',
    color: theme.palette.text.primary,
  },
  filterIcon: {
    marginRight: theme.spacing(0.5),
    fontSize: '1rem',
    color: theme.palette.primary.main,
  },
  dateSeparator: {
    margin: 0,
    alignSelf: 'center',
    color: theme.palette.text.secondary,
    fontSize: '0.85rem'
  },
  collapseTransition: {
    transition: 'all 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) !important',
  },
  divider: {
    margin: theme.spacing(1, 0),
  },
  selectControl: {
    minWidth: 200,
    height: 34,
    '& .MuiInputLabel-shrink': {
      transform: 'translate(14px, -6px) scale(0.75)',
    },
    '& .MuiSelect-select': {
      paddingTop: 9,
      paddingBottom: 9,
    },
    "& .MuiOutlinedInput-notchedOutline": {
      borderRadius: 8,
    },
  },
  sectionIcon: {
    marginRight: theme.spacing(0.5),
    marginTop: theme.spacing(-0.5),
    fontSize: '1rem',
    verticalAlign: 'middle',
  },
  refreshRow: {
    display: 'flex',
    alignItems: 'center',
  },
  refreshIntervalLabel: {
    display: 'flex',
    alignItems: 'center',
    marginRight: theme.spacing(1),
    '& .MuiSvgIcon-root': {
      marginRight: theme.spacing(0.5),
      fontSize: '1rem',
    },
  },
  sectionControlsTitle: {
    display: 'flex',
    alignItems: 'center',
    fontSize: '0.85rem',
    fontWeight: 600,
    color: theme.palette.text.primary,
    marginBottom: theme.spacing(1),
  },
  sectionControls: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  sectionChip: {
    margin: theme.spacing(0.5),
    transition: 'all 0.2s ease',
    borderRadius: 16,
    height: 32,
    fontWeight: 500,
    boxShadow: 'none',
    border: `1px solid ${theme.palette.divider}`,
  },
  sectionChipActive: {
    backgroundColor: theme.palette.type === 'dark'
      ? `${theme.palette.primary.main}20`
      : `${theme.palette.primary.main}10`,
    borderColor: theme.palette.primary.main,
    '& .MuiChip-icon, & .MuiChip-label': {
      color: theme.palette.primary.main,
    },
  },
  sectionChipInactive: {
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(255, 255, 255, 0.05)'
      : 'rgba(0, 0, 0, 0.05)',
    opacity: 0.7,
    '& .MuiChip-icon, & .MuiChip-label': {
      color: theme.palette.text.secondary,
    },
  },
  sectionActions: {
    display: 'flex',
    gap: theme.spacing(1),
    marginTop: theme.spacing(1),
  },
  sectionActionButton: {
    fontSize: '0.75rem',
    padding: theme.spacing(0.5, 1),
    minWidth: 'auto',
    borderRadius: 16,
    textTransform: 'none',
    fontWeight: 500,
  },
  sectionActionIcon: {
    fontSize: '0.875rem',
    marginRight: theme.spacing(0.5),
  },
  sectionChipIcon: {
    color: theme.palette.type === 'dark'
      ? theme.palette.text.secondary
      : theme.palette.text.primary,
  },
  masterSectionControl: {
    marginBottom: theme.spacing(1),
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
  },
  toggleAllButton: {
    fontSize: '0.75rem',
    padding: theme.spacing(0.5, 1),
    borderRadius: 16,
    textTransform: 'none',
    fontWeight: 500,
    marginLeft: theme.spacing(1),
  },
  visibilityChipAvatar: {
    backgroundColor: theme.palette.type === 'dark'
      ? 'rgba(255, 255, 255, 0.08)'
      : theme.palette.grey[200],
    color: theme.palette.type === 'dark'
      ? theme.palette.text.secondary
      : theme.palette.grey[700],
    '&.MuiChip-avatarColorPrimary': {
      backgroundColor: theme.palette.type === 'dark'
        ? `${theme.palette.primary.dark}80`
        : theme.palette.primary.light,
      color: theme.palette.type === 'dark'
        ? theme.palette.primary.light
        : theme.palette.primary.contrastText,
    },
  },
}));

const safelyParseDate = (dateString) => {
  if (!dateString) return null;

  try {
    const [year, month, day] = dateString.split('-').map(Number);
    if (isNaN(year) || isNaN(month) || isNaN(day)) return null;

    return new Date(year, month - 1, day, 12, 0, 0);
  } catch (error) {
    console.error("Erro ao converter data:", error);
    return null;
  }
};

const ModernFilters = ({
  dateStartTicket,
  dateEndTicket,
  setDateStartTicket,
  setDateEndTicket,
  setQueueTicket,
  fetchData,
  visibleSections,
  setVisibleSections,
  refreshInterval,
  setRefreshInterval,
}) => {
  const classes = useStyles();
  const [dateStart, setDateStart] = useState(dateStartTicket);
  const [dateEnd, setDateEnd] = useState(dateEndTicket);
  const [activeQuickFilter, setActiveQuickFilter] = useState('last7Days');
  const isMounted = useMountedState();
  const { filterVisible } = useDashboardContext();

  const updateBothDateStates = useCallback((startDate, endDate) => {
    setDateStart(startDate);
    setDateEnd(endDate);
    setDateStartTicket(startDate);
    setDateEndTicket(endDate);
  }, [setDateStartTicket, setDateEndTicket]);

  const handleLast7Days = useCallback(() => {
    if (!isMounted.current) return;

    setActiveQuickFilter('last7Days');

    const today = new Date();
    const last7Days = subDays(today, 6);
    const startDateFormatted = format(last7Days, 'yyyy-MM-dd');
    const endDateFormatted = format(today, 'yyyy-MM-dd');

    updateBothDateStates(startDateFormatted, endDateFormatted);
    setQueueTicket("");
    fetchData(prev => !prev);
  }, [isMounted, updateBothDateStates, setQueueTicket, fetchData]);

  const updateActiveQuickFilter = useCallback(() => {
    if (dateStartTicket && dateEndTicket) {
      const today = new Date();
      const formattedToday = format(today, 'yyyy-MM-dd');
      const yesterday = format(subDays(today, 1), 'yyyy-MM-dd');
      const startOfWeekDate = format(startOfWeek(today, { weekStartsOn: 0 }), 'yyyy-MM-dd');
      const endOfWeekDate = format(endOfWeek(today, { weekStartsOn: 0 }), 'yyyy-MM-dd');
      const startOfMonthDate = format(startOfMonth(today), 'yyyy-MM-dd');
      const initialDate = '2023-08-01';

      const currentMonth = today.getMonth();
      const lastMonthDate = new Date(today);
      lastMonthDate.setMonth(currentMonth - 1);
      const firstDayLastMonth = new Date(lastMonthDate.getFullYear(), lastMonthDate.getMonth(), 1);
      const lastDayLastMonth = new Date(lastMonthDate.getFullYear(), lastMonthDate.getMonth() + 1, 0);
      const lastMonthStart = format(firstDayLastMonth, 'yyyy-MM-dd');
      const lastMonthEnd = format(lastDayLastMonth, 'yyyy-MM-dd');

      if (dateStartTicket === formattedToday && dateEndTicket === formattedToday) {
        setActiveQuickFilter('today');
      } else if (dateStartTicket === yesterday && dateEndTicket === yesterday) {
        setActiveQuickFilter('yesterday');
      } else if (dateStartTicket === startOfWeekDate && dateEndTicket === endOfWeekDate) {
        setActiveQuickFilter('thisWeek');
      } else if (dateStartTicket === startOfMonthDate && dateEndTicket === formattedToday) {
        setActiveQuickFilter('thisMonth');
      } else if (dateStartTicket === lastMonthStart && dateEndTicket === lastMonthEnd) {
        setActiveQuickFilter('lastMonth');
      } else if (dateStartTicket === format(subDays(today, 6), 'yyyy-MM-dd') && dateEndTicket === formattedToday) {
        setActiveQuickFilter('last7Days');
      } else if (dateStartTicket === initialDate && dateEndTicket === formattedToday) {
        setActiveQuickFilter('allTime');
      } else {
        setActiveQuickFilter(null);
      }
    }
  }, [dateStartTicket, dateEndTicket]);

  useEffect(() => {
    if (dateStartTicket && dateEndTicket) {
      setDateStart(dateStartTicket);
      setDateEnd(dateEndTicket);
    }
  }, [dateStartTicket, dateEndTicket]);

  useEffect(() => {
    if (isMounted.current) {
      updateActiveQuickFilter();
    }
  }, [dateStartTicket, dateEndTicket, updateActiveQuickFilter, isMounted]);

  useEffect(() => {
    const savedSections = localStorage.getItem('dashboardVisibleSections');
    const savedInterval = localStorage.getItem('dashboardRefreshInterval');

    if (savedSections && typeof setVisibleSections === 'function') {
      try {
        const parsedSections = JSON.parse(savedSections);
        setVisibleSections(parsedSections);
      } catch (error) {
        console.error('Erro ao carregar seções do localStorage:', error);
      }
    }

    if (savedInterval && typeof setRefreshInterval === 'function') {
      setRefreshInterval(savedInterval);
    }

    if (!dateStartTicket || !dateEndTicket) {
      setTimeout(() => {
        handleLast7Days();
      }, 0);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (visibleSections) {
      localStorage.setItem('dashboardVisibleSections', JSON.stringify(visibleSections));
    }
  }, [visibleSections]);

  useEffect(() => {
    if (refreshInterval) {
      localStorage.setItem('dashboardRefreshInterval', refreshInterval);
    }
  }, [refreshInterval]);

  const handleAllTime = useCallback(() => {
    if (!isMounted.current) return;

    setActiveQuickFilter('allTime');
    const startDateFormatted = '2023-08-01';
    const endDateFormatted = format(new Date(), 'yyyy-MM-dd');

    updateBothDateStates(startDateFormatted, endDateFormatted);
    setQueueTicket("");
    fetchData(prev => !prev);
  }, [isMounted, updateBothDateStates, setQueueTicket, fetchData]);

  const handleToday = useCallback(() => {
    if (!isMounted.current) return;

    setActiveQuickFilter('today');
    const today = new Date();
    const dateFormatted = format(today, 'yyyy-MM-dd');

    updateBothDateStates(dateFormatted, dateFormatted);
    setQueueTicket("");
    fetchData(prev => !prev);
  }, [isMounted, updateBothDateStates, setQueueTicket, fetchData]);

  const handleYesterday = useCallback(() => {
    if (!isMounted.current) return;

    setActiveQuickFilter('yesterday');
    const yesterday = subDays(new Date(), 1);
    const dateFormatted = format(yesterday, 'yyyy-MM-dd');

    updateBothDateStates(dateFormatted, dateFormatted);
    setQueueTicket("");
    fetchData(prev => !prev);
  }, [isMounted, updateBothDateStates, setQueueTicket, fetchData]);

  const handleThisMonth = useCallback(() => {
    if (!isMounted.current) return;

    setActiveQuickFilter('thisMonth');
    const today = new Date();
    const startDateFormatted = format(startOfMonth(today), 'yyyy-MM-dd');
    const endDateFormatted = format(today, 'yyyy-MM-dd');

    updateBothDateStates(startDateFormatted, endDateFormatted);
    setQueueTicket("");
    fetchData(prev => !prev);
  }, [isMounted, updateBothDateStates, setQueueTicket, fetchData]);

  const handleLastMonth = useCallback(() => {
    if (!isMounted.current) return;

    setActiveQuickFilter('lastMonth');
    const today = new Date();
    const currentMonth = today.getMonth();
    const lastMonthDate = new Date(today);
    lastMonthDate.setMonth(currentMonth - 1);
    const firstDayLastMonth = new Date(lastMonthDate.getFullYear(), lastMonthDate.getMonth(), 1);
    const lastDayLastMonth = new Date(lastMonthDate.getFullYear(), lastMonthDate.getMonth() + 1, 0);
    const startDateFormatted = format(firstDayLastMonth, 'yyyy-MM-dd');
    const endDateFormatted = format(lastDayLastMonth, 'yyyy-MM-dd');

    updateBothDateStates(startDateFormatted, endDateFormatted);
    setQueueTicket("");
    fetchData(prev => !prev);
  }, [isMounted, updateBothDateStates, setQueueTicket, fetchData]);

  const handleThisWeek = useCallback(() => {
    if (!isMounted.current) return;

    setActiveQuickFilter('thisWeek');
    const today = new Date();
    const startDateFormatted = format(startOfWeek(today, { weekStartsOn: 0 }), 'yyyy-MM-dd');
    const endDateFormatted = format(endOfWeek(today, { weekStartsOn: 0 }), 'yyyy-MM-dd');

    updateBothDateStates(startDateFormatted, endDateFormatted);
    setQueueTicket("");
    fetchData(prev => !prev);
  }, [isMounted, updateBothDateStates, setQueueTicket, fetchData]);

  const handleDateStartChange = useCallback((date) => {
    if (!isMounted.current) return;

    if (date && !isNaN(date.getTime())) {
      setDateStart(format(date, 'yyyy-MM-dd'));
      setActiveQuickFilter(null);
    }
  }, [isMounted]);

  const handleDateEndChange = useCallback((date) => {
    if (!isMounted.current) return;

    if (date && !isNaN(date.getTime())) {
      setDateEnd(format(date, 'yyyy-MM-dd'));
      setActiveQuickFilter(null);
    }
  }, [isMounted]);

  const handleApplyFilters = useCallback(() => {
    if (!isMounted.current) return;

    setDateStartTicket(dateStart);
    setDateEndTicket(dateEnd);
    setQueueTicket("");
    fetchData(prev => !prev);
  }, [isMounted, dateStart, dateEnd, setDateStartTicket, setDateEndTicket, setQueueTicket, fetchData]);

  const handleResetFilters = useCallback(() => {
    if (!isMounted.current) return;
    handleLast7Days();
  }, [isMounted, handleLast7Days]);

  const handleSectionToggle = useCallback((section) => {
    if (typeof setVisibleSections === 'function') {
      setVisibleSections(prev => ({
        ...prev,
        [section]: !prev[section]
      }));
    }
  }, [setVisibleSections]);

  const handleRefreshIntervalChange = useCallback((e) => {
    if (typeof setRefreshInterval === 'function') {
      setRefreshInterval(e.target.value);
    }
  }, [setRefreshInterval]);

  const toggleAllSections = useCallback((show) => {
    if (typeof setVisibleSections === 'function') {
      setVisibleSections({
        metrics: show,
        overview: show,
        messages: show,
        attendants: show,
        distribution: show,
        hourly: show
      });
    }
  }, [setVisibleSections]);

  const allSectionsVisible = useMemo(() => {
    return visibleSections &&
      visibleSections.metrics &&
      visibleSections.overview &&
      visibleSections.messages &&
      visibleSections.attendants &&
      visibleSections.distribution &&
      visibleSections.hourly;
  }, [visibleSections]);

  const noSectionsVisible = useMemo(() => {
    return visibleSections &&
      !visibleSections.metrics &&
      !visibleSections.overview &&
      !visibleSections.messages &&
      !visibleSections.attendants &&
      !visibleSections.distribution &&
      !visibleSections.hourly;
  }, [visibleSections]);

  const sectionChips = [
    {
      key: 'metrics',
      label: i18n.t("dashboard.general.title") || "Informações Gerais",
      icon: <DashboardIcon className={classes.sectionChipIcon} fontSize="small" />
    },
    {
      key: 'overview',
      label: i18n.t("dashboard.overview.title") || "Visão Geral",
      icon: <PieChartIcon className={classes.sectionChipIcon} fontSize="small" />
    },
    {
      key: 'messages',
      label: i18n.t("dashboard.messages.title") || "Mensagens",
      icon: <TimelineIcon className={classes.sectionChipIcon} fontSize="small" />
    },
    {
      key: 'attendants',
      label: i18n.t("dashboard.tabs.attendants") || "Atendentes",
      icon: <PeopleIcon className={classes.sectionChipIcon} fontSize="small" />
    },
    {
      key: 'distribution',
      label: i18n.t("dashboard.distribution.title") || "Distribuição",
      icon: <CategoryIcon className={classes.sectionChipIcon} fontSize="small" />
    },
    {
      key: 'hourly',
      label: i18n.t("dashboard.hourly.title") || "Atividade por Hora",
      icon: <ScheduleIcon className={classes.sectionChipIcon} fontSize="small" />
    }
  ];

  return (
    <Collapse in={filterVisible} className={classes.collapseTransition}>
      <Paper className={classes.filterContainer}>
        <div className={classes.filterHeader}>
          <Typography className={classes.filterTitle}>
            <FilterListIcon className={classes.filterIcon} />
            {i18n.t("dashboard.filters.title") || "Filtros"}
          </Typography>
          <Tooltip title={i18n.t("dashboard.filters.reset") || "Limpar Filtros"}>
            <IconButton
              size="small"
              className={classes.resetButton}
              onClick={handleResetFilters}
            >
              <RestoreIcon fontSize="small" />
            </IconButton>
          </Tooltip>
        </div>

        <Grid container spacing={1}>
          <Grid item xs={12}>
            <div className={classes.quickFilterContainer}>
              <Chip
                size="small"
                label={i18n.t("dashboard.filters.today") || "Hoje"}
                clickable
                onClick={handleToday}
                className={`${classes.chip} ${activeQuickFilter === 'today' ? classes.activeChip : ''}`}
              />
              <Chip
                size="small"
                label={i18n.t("dashboard.filters.yesterday") || "Ontem"}
                clickable
                onClick={handleYesterday}
                className={`${classes.chip} ${activeQuickFilter === 'yesterday' ? classes.activeChip : ''}`}
              />
              <Chip
                size="small"
                label={i18n.t("dashboard.filters.last7days") || "7 dias"}
                clickable
                onClick={handleLast7Days}
                className={`${classes.chip} ${activeQuickFilter === 'last7Days' ? classes.activeChip : ''}`}
              />
              <Chip
                size="small"
                label={i18n.t("dashboard.filters.thisWeek") || "Esta semana"}
                clickable
                onClick={handleThisWeek}
                className={`${classes.chip} ${activeQuickFilter === 'thisWeek' ? classes.activeChip : ''}`}
              />
              <Chip
                size="small"
                label={i18n.t("dashboard.filters.thisMonth") || "Este mês"}
                clickable
                onClick={handleThisMonth}
                className={`${classes.chip} ${activeQuickFilter === 'thisMonth' ? classes.activeChip : ''}`}
              />
              <Chip
                size="small"
                label={i18n.t("dashboard.filters.lastMonth") || "Mês passado"}
                clickable
                onClick={handleLastMonth}
                className={`${classes.chip} ${activeQuickFilter === 'lastMonth' ? classes.activeChip : ''}`}
              />
              <Chip
                size="small"
                label={i18n.t("dashboard.filters.allTime") || "Todo período"}
                clickable
                onClick={handleAllTime}
                className={`${classes.chip} ${activeQuickFilter === 'allTime' ? classes.activeChip : ''}`}
              />
            </div>
          </Grid>

          <Grid item xs={12}>
            <Box className={classes.dateRow}>
              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={brLocale}>
                <DatePicker
                  value={safelyParseDate(dateStart)}
                  onChange={handleDateStartChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder={i18n.t("dashboard.date.initialDate") || "Data inicial"}
                      variant="outlined"
                      className={classes.datePickerField}
                    />
                  )}
                />
              </LocalizationProvider>

              <Typography variant="body2" className={classes.dateSeparator}>até</Typography>

              <LocalizationProvider dateAdapter={AdapterDateFns} adapterLocale={brLocale}>
                <DatePicker
                  value={safelyParseDate(dateEnd)}
                  onChange={handleDateEndChange}
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      size="small"
                      placeholder={i18n.t("dashboard.date.finalDate") || "Data final"}
                      variant="outlined"
                      className={classes.datePickerField}
                    />
                  )}
                />
              </LocalizationProvider>

              <Button
                variant="contained"
                className={classes.applyButton}
                onClick={handleApplyFilters}
                startIcon={<SearchIcon />}
                size="small"
              >
                {i18n.t("dashboard.filters.apply") || "Filtrar"}
              </Button>
            </Box>
          </Grid>

          <Grid item xs={12}>
            <Divider className={classes.divider} />
          </Grid>

          <Grid item xs={12}>
            <div className={classes.masterSectionControl}>
              <Typography className={classes.sectionControlsTitle}>
                <ViewDayIcon className={classes.filterIcon} />
                {i18n.t("dashboard.filters.sections") || "Seções Visíveis"}
              </Typography>

              <Box>
                <Button
                  size="small"
                  variant="outlined"
                  color="primary"
                  className={classes.toggleAllButton}
                  onClick={() => toggleAllSections(true)}
                  startIcon={<VisibilityIcon fontSize="small" />}
                  disabled={allSectionsVisible}
                >
                  {i18n.t("dashboard.filters.showAll") || "Mostrar Todas"}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  className={classes.toggleAllButton}
                  onClick={() => toggleAllSections(false)}
                  startIcon={<VisibilityOffIcon fontSize="small" />}
                  disabled={noSectionsVisible}
                >
                  {i18n.t("dashboard.filters.hideAll") || "Ocultar Todas"}
                </Button>
              </Box>
            </div>

            {/* ✅ CORRIGIDO: Removido Fade para evitar warning do StrictMode com findDOMNode */}
            <div className={classes.sectionControls}>
                {sectionChips.map((section) => (
                  <Chip
                    key={section.key}
                    label={section.label}
                    clickable
                    onClick={() => handleSectionToggle(section.key)}
                    className={`${classes.sectionChip} ${visibleSections[section.key]
                      ? classes.sectionChipActive
                      : classes.sectionChipInactive
                      }`}
                    variant={visibleSections[section.key] ? "default" : "outlined"}
                    avatar={
                      <Avatar
                        className={classes.visibilityChipAvatar}
                        color={visibleSections[section.key] ? "primary" : "default"}
                      >
                        {visibleSections[section.key]
                          ? <VisibilityIcon fontSize="small" />
                          : <VisibilityOffIcon fontSize="small" />
                        }
                      </Avatar>
                    }
                  />
                ))}
              </div>
            {/* </Fade> - Removido para evitar warning do StrictMode */}
          </Grid>

          <Grid item xs={12}>
            <Divider className={classes.divider} />
          </Grid>

          <Grid item xs={12}>
            <div className={classes.refreshRow}>
              <Typography className={classes.refreshIntervalLabel}>
                <UpdateIcon />
                {i18n.t("dashboard.filters.refreshInterval") || "Intervalo de atualização:"}
              </Typography>
              <FormControl variant="outlined" size="small" className={classes.selectControl}>
                <Select
                  value={refreshInterval || 'manual'}
                  onChange={handleRefreshIntervalChange}
                >
                  <MenuItem value="manual">{i18n.t("dashboard.filters.refreshManual") || "Manual"}</MenuItem>
                  <MenuItem value="5">{i18n.t("dashboard.filters.refresh5min") || "5 minutos"}</MenuItem>
                  <MenuItem value="15">{i18n.t("dashboard.filters.refresh15min") || "15 minutos"}</MenuItem>
                  <MenuItem value="30">{i18n.t("dashboard.filters.refresh30min") || "30 minutos"}</MenuItem>
                </Select>
              </FormControl>
            </div>
          </Grid>
        </Grid>
      </Paper>
    </Collapse>
  );
};

export default ModernFilters;